import { Injectable, REQUEST as ANGULAR_REQUEST, inject } from '@angular/core';
import { REQUEST } from '../../../../express.tokens';
// import * as moment from 'moment';
// import 'moment/locale/he';
import { AppGlobalsModule, AppGlobalsModule as _globals } from 'src/app/app-globals.module';
import { YeshivaEnums } from 'src/serverTypes/yeshivaTypesEnums';
import { BrowserPlatformService } from '../browser-platform/browser-platform.service';
import { isNihulSurfaceUrl } from 'src/app/nihul/nihul-surfaces';

@Injectable({
    providedIn: 'root',
})
export class I18nService {
    private _request = inject(REQUEST, { optional: true });
    private _angularRequest = inject(ANGULAR_REQUEST, { optional: true });
    private _browser = inject(BrowserPlatformService);

    private _currLang = '';

    constructor() {
        // moment.locale(this.getLangshort());
    }

    public svgAssetsFullPathAutoLang(fileName: string): string {
        return _globals.svgAssetsFullPath(this.getLangshort()) + fileName;
    }

    public getProdDomain() {
        return 'https://www.yeshiva.' + (this.langIsHeb() ? 'org.il' : 'co');
    }

    public imgNotAngularAssetsFullPathAutoLang(fileName: string): string {
        return _globals.imgNotAngularAssetsFullPath(this.getLangshort()) + fileName;
    }

    public getLangBaseUrl(langIsHeb?: boolean): string {
        if (langIsHeb === undefined) {
            langIsHeb = this.langIsHeb();
        }

        const protocol: string = (!AppGlobalsModule.isDevelopment ? 'https' : 'http') + '://';
        const urlProd: string = langIsHeb ? 'www.yeshiva.org.il' : 'www.yeshiva.co';
        const urlDev: string = langIsHeb ? 'he.local' : 'en.local';

        // In dev mode we need to calculate the port
        if (AppGlobalsModule.isDevelopment) {
            const requestPort = this._getRequestHost()?.split(':')[1];
            const port = requestPort ?? this._browser.location?.port;
            return protocol + urlDev + this._getDevPortForLang(port, langIsHeb);
        } else {
            return protocol + urlProd;
        }
    }

    private _getDevPortForLang(port: string | undefined, langIsHeb: boolean): string {
        if (!port) {
            return '';
        }

        const oppositeLangPorts: Record<string, string> = {
            '4201': '4202',
            '4202': '4201',
            '4203': '4204',
            '4204': '4203',
        };
        const mappedPort = oppositeLangPorts[port];

        if (!mappedPort) {
            return ':' + port;
        }

        const portIsHeb = port === '4201' || port === '4203';
        if (portIsHeb === langIsHeb) {
            return ':' + port;
        }

        return ':' + mappedPort;
    }

    public getOpositeLangBaseUrl(): string {
        // Flips the current language
        return this.getLangBaseUrl(!this.langIsHeb());
    }

    private _setLang() {
        if (this._currLang.length > 0) {
            return;
        }
        this._currLang = 'heb';

        //server
        // _request is being set up in server.ts and it's IGetLangRequest so it has lang property
        if (this._request && (this._request as any).lang && (this._request as any).lang.toLowerCase() === 'eng') {
            this._currLang = 'eng';
        }

        if (AppGlobalsModule.isDevelopment) {
            const requestHost = this._getRequestHost();
            const langFromRequestHost = requestHost ? this.getLangFromUrl(requestHost, requestHost.split(':')[0]) : undefined;
            if (langFromRequestHost === 'eng') {
                this._currLang = 'eng';
            } else if (langFromRequestHost === 'heb') {
                this._currLang = 'heb';
            } else if (AppGlobalsModule.isServer && $localize`:@@bundle-lang:he` === 'en') {
                //the dev server prerenders (ssg): no REQUEST token - the bundle's own translation tells the language
                this._currLang = 'eng';
            }
        }

        //client check for eng based on current location
        const location = this._browser.location;
        if (location?.href) {
            //a nihul surface is the hebrew app on the hebrew host whatever its query says: its
            //?lang= is the EDITED CONTENT's language (nihul-client.md), not the app's. Reading it
            //here made a /nihul...?lang=en entry run the whole spa session as 'eng': every request
            //then carried Lang: eng on the hebrew origin, varnish answered them from the english
            //site's objects and the cdn stored those english bodies under the HEBREW urls, so
            //every hebrew editor saw english rabbi/category lists (cache-flow.md item 11)
            const langFromUrl = isNihulSurfaceUrl(location.pathname ?? '')
                ? this.getLangFromUrl(location.hostname, location.hostname)
                : this.getLangFromUrl(location.href, location.hostname);
            if (langFromUrl === 'eng') {
                this._currLang = 'eng';
            }
        }
    }

    private _getRequestHost(): string | undefined {
        const expressHost = this._request?.headers?.host;
        if (typeof expressHost === 'string') {
            return expressHost;
        }

        const angularHost = this._angularRequest?.headers?.get('host');
        if (angularHost) {
            return angularHost;
        }

        const angularUrl = this._angularRequest?.url;
        if (!angularUrl) {
            return undefined;
        }

        try {
            return new URL(angularUrl).host;
        } catch {
            return undefined;
        }
    }

    /**
     * Detect the language of a given URL. Returns 'eng', 'heb', or undefined if it can't be determined.
     * Optionally pass a pre-parsed hostname (used by _setLang to avoid re-parsing window.location).
     */
    public getLangFromUrl(url: string, hostname?: string): 'heb' | 'eng' | undefined {
        if (!url) return undefined;

        if (url.match(/la?ng=en/i)) {
            return 'eng';
        }
        if (url.match(/la?ng=he/i)) {
            return 'heb';
        }

        let host = hostname;
        if (!host) {
            // Accept absolute (https://...), protocol-relative (//...), and bare (www...) URLs.
            const m = url.match(/^(?:https?:)?\/\/([^/:?#]+)/i) ?? url.match(/^([^/:?#]+)/i);
            host = m?.[1];
        }
        if (host) {
            host = host.toLowerCase();
            if (host.startsWith('en.') || host.endsWith('.co') || host === 'yeshiva.co') {
                return 'eng';
            }
            if (host.startsWith('he.') || host.endsWith('.org.il') || host === 'yeshiva.org.il') {
                return 'heb';
            }
        }
        return undefined;
    }

    public getLang() {
        this._setLang();
        return this._currLang;
    }

    public getLangshort() {
        return this.getLang().substring(0, 2);
    }

    public getLangClass() {
        return `Lang${this.getLangshort().toUpperCase()}`;
    }

    public langIsHeb(): boolean {
        return this.getLang() === 'heb';
    }

    public getLangDigit() {
        return YeshivaEnums.YeshivaModels.Logic.Globalization.Lang[this.langIsHeb() ? 'He' : 'En'];
    }
}
